import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from 'react';
import { API_URL } from '../config';
import logger from '../utils/logger';

const CartContext = createContext();

const CART_STORAGE_KEY = 'trventilation_cart';
const DISCOUNT_STORAGE_KEY = 'appliedDiscount';

export const CartProvider = ({ children }) => {
  // Load cart from localStorage on initialization
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (error) {
      logger.error('Failed to load cart from localStorage:', error);
      return [];
    }
  });

  const [appliedDiscount, setAppliedDiscount] = useState(() => {
    try {
      const raw = localStorage.getItem(DISCOUNT_STORAGE_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [freeShippingThreshold, setFreeShippingThreshold] = useState(750);

  // Fetch free shipping threshold from site settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await fetch(`${API_URL}/site-settings`);
        if (response.ok) {
          const settings = await response.json();
          const threshold = settings.shipping?.freeShippingThreshold || 750;
          setFreeShippingThreshold(threshold);
        }
      } catch (error) {
        logger.error('Failed to fetch site settings:', error);
      }
    };

    fetchSettings();
  }, []);

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
    } catch (error) {
      logger.error('Failed to save cart to localStorage:', error);
    }
  }, [cart]);

  // Add to cart (supports multiple quantities and variants)
  const addToCart = useCallback((product) => {
    setCart((prevCart) => {
      // Use variantId if product has variants, otherwise use id, otherwise use _id
      const uniqueId = product.variantId || product.id || product._id;
      const existingItem = prevCart.find((item) => {
        const itemId = item.variantId || item.id || item._id;
        // Check if items match (including cascading tiers for uniqueness)
        const itemsMatch =
          itemId === uniqueId &&
          item.selectedSize === product.selectedSize &&
          item.selectedColor === product.selectedColor &&
          item.selectedMaterial === product.selectedMaterial &&
          item.selectedDiameter === product.selectedDiameter &&
          item.cascadingTier1 === product.cascadingTier1 &&
          item.cascadingTier2 === product.cascadingTier2;
        return itemsMatch;
      });

      if (existingItem) {
        return prevCart.map((item) => {
          const itemId = item.variantId || item.id || item._id;
          // Check if items match (including cascading tiers for uniqueness)
          const itemsMatch =
            itemId === uniqueId &&
            item.selectedSize === product.selectedSize &&
            item.selectedColor === product.selectedColor &&
            item.selectedMaterial === product.selectedMaterial &&
            item.selectedDiameter === product.selectedDiameter &&
            item.cascadingTier1 === product.cascadingTier1 &&
            item.cascadingTier2 === product.cascadingTier2;
          return itemsMatch
            ? { ...item, quantity: item.quantity + (product.quantity || 1) }
            : item;
        });
      } else {
        return [...prevCart, { ...product, quantity: product.quantity || 1 }];
      }
    });
  }, []);

  // Remove item from cart (supports variants)
  const removeFromCart = useCallback((id) => {
    setCart((prevCart) => prevCart.filter((item) => {
      const itemId = item.variantId || item.id || item._id;
      return itemId !== id;
    }));
  }, []);

  // Update quantity directly (supports variants)
  const updateQuantity = useCallback((id, quantity) => {
    setCart((prevCart) =>
      prevCart.map((item) => {
        const itemId = item.variantId || item.id || item._id;
        return itemId === id ? { ...item, quantity } : item;
      })
    );
  }, []);

  // Clear the entire cart
  const clearCart = useCallback(() => {
    setCart([]);
    // Also clear any applied discount to prevent it from carrying over to the next order
    setAppliedDiscount(null);
    localStorage.removeItem(DISCOUNT_STORAGE_KEY);
  }, []);

  // Apply discount code by validating with server
  const applyDiscount = useCallback(async ({ code, orderTotal = 0, customerEmail = '' }) => {
    try {
      const res = await fetch(`${API_URL}/discount-codes/validate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, orderTotal, customerEmail })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Invalid discount code');
      }

      const discount = {
        code: data.code,
        type: data.type,
        value: data.value,
        discountAmount: data.discountAmount || 0,
        freeShipping: data.freeShipping || false,
        description: data.description || ''
      };

      setAppliedDiscount(discount);
      localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(discount));
      return { success: true, discount };
    } catch (error) {
      return { success: false, error: error.message };
    }
  }, []);

  const removeDiscount = useCallback(() => {
    setAppliedDiscount(null);
    localStorage.removeItem(DISCOUNT_STORAGE_KEY);
  }, []);

  // Apply trade account discount (automatic)
  const applyTradeAccountDiscount = useCallback((discountPercentage) => {
    if (discountPercentage <= 0) {
      removeDiscount();
      return;
    }

    const discount = {
      code: 'TRADE_ACCOUNT',
      type: 'percentage',
      value: discountPercentage,
      isTradeAccountDiscount: true,
      description: 'Trade Account Discount Applied',
      discountAmount: 0 // Will be calculated at checkout
    };

    setAppliedDiscount(discount);
    localStorage.setItem(DISCOUNT_STORAGE_KEY, JSON.stringify(discount));
  }, [removeDiscount]);

  // Calculate subtotal and free shipping status
  const subtotal = useMemo(() => {
    return cart.reduce((sum, item) => sum + item.price * item.quantity, 0);
  }, [cart]);

  const isFreeShippingActive = useMemo(() => {
    return subtotal >= freeShippingThreshold;
  }, [subtotal, freeShippingThreshold]);

  // Memoize context value to prevent unnecessary re-renders
  const value = useMemo(() => ({
    cart,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    appliedDiscount,
    applyDiscount,
    removeDiscount,
    applyTradeAccountDiscount,
    subtotal,
    isFreeShippingActive,
    freeShippingThreshold
  }), [cart, addToCart, removeFromCart, updateQuantity, clearCart, appliedDiscount, applyDiscount, removeDiscount, applyTradeAccountDiscount, subtotal, isFreeShippingActive, freeShippingThreshold]);

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};

// Custom hook for easier usage
export const useCart = () => useContext(CartContext);

export default CartContext;